import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { FaWarehouse, FaEdit, FaComments, FaUser, FaBox } from 'react-icons/fa';
import './WarehouseConversation.css';
import axios from 'axios';
import Button from './UI/Button';

/**
 * Main WarehouseConversation component.
 * Fetches and displays warehouse conversation data for a ticket.
 */
const WarehouseConversation = ({ ticketId, onRefresh }) => {
  const { t } = useTranslation();
  const [conversation, setConversation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editedParams, setEditedParams] = useState({});
  const [editedMessage, setEditedMessage] = useState('');

  useEffect(() => {
    if (ticketId) {
      fetchWarehouseConversation();
    }
  }, [ticketId]);

  const fetchWarehouseConversation = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get(`/api/tickets/warehouse_conversation/${ticketId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setConversation(response.data);
    } catch (error) {
      console.error('Failed to fetch warehouse conversation:', error);
      setError(t('warehouseConversation.fetchError', 'Failed to load warehouse conversation'));
    } finally {
      setLoading(false);
    }
  };

  const formatTimestamp = (timestamp) => {
    return new Date(timestamp).toLocaleString();
  };

  const handleEditClick = () => {
    setIsEditing(true);
    setEditedParams(conversation.interaction_params || {});
    setEditedMessage(conversation.messages?.[0]?.content || '');
  };

  const handleSaveEdit = async () => {
    try {
      const token = localStorage.getItem('access_token');
      await axios.put(
        `/api/tickets/warehouse_conversation/${ticketId}/update`,
        {
          interaction_params: editedParams,
          first_message: editedMessage
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      setIsEditing(false);
      await fetchWarehouseConversation();
      if (onRefresh) {
        onRefresh();
      }
    } catch (error) {
      console.error('Failed to update warehouse conversation:', error);
      setError(t('warehouseConversation.updateError', 'Failed to update warehouse conversation'));
    }
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditedParams(conversation.interaction_params || {});
    setEditedMessage(conversation.messages?.[0]?.content || '');
  };

  const parseWarehouseResponse = (content) => {
    if (!content) return { details: '' };
    
    // Parse warehouse response content
    const lines = content.split('\n');
    const response = lines
      .filter(line => !line.trim().startsWith('>'))
      .join('\n')
      .trim();

    return {
      details: response || content
    };
  };

  if (loading) {
    return (
      <div className="warehouse-conversation-loading">
        <FaBox className="loading-icon" />
        <p>{t('warehouseConversation.loading', 'Loading warehouse conversation...')}</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="warehouse-conversation-error">
        <p>{error}</p>
        <Button variant="tertiary" onClick={fetchWarehouseConversation}>
          {t('warehouseConversation.retry', 'Retry')}
        </Button>
      </div>
    );
  }

  // If no conversation exists, show empty state
  if (!conversation || (!conversation.messages || conversation.messages.length === 0)) {
    return (
      <div className="warehouse-conversation-empty">
        <FaWarehouse className="empty-icon" />
        <h3>{t('warehouseConversation.noConversation', 'No Warehouse Conversation')}</h3>
        <p>{t('warehouseConversation.noConversationDescription', 'No warehouse conversation has been initiated for this ticket yet.')}</p>
      </div>
    );
  }

  const displayParams = conversation.interaction_params || {};

  return (
    <div className="warehouse-conversation">
      <div className="warehouse-conversation-header">
        <FaWarehouse className="warehouse-icon" />
        <h3>{t('warehouseConversation.title', 'Warehouse Conversation')}</h3>
      </div>

      <div className="warehouse-conversation-content">
        {Object.keys(displayParams).length > 0 && (
          <div className="warehouse-conversation-params">
            {isEditing ? (
              <>
                {Object.entries(editedParams).map(([key, value]) => (
                  <div key={key} className="param-item">
                    <span className="param-label">{t(`warehouseConversation.${key}`) || key}:</span>
                    <input
                      type="text"
                      value={value || ''}
                      onChange={(e) =>
                        setEditedParams({
                          ...editedParams,
                          [key]: e.target.value
                        })
                      }
                      className="param-input"
                    />
                  </div>
                ))}
                <div className="edit-actions">
                  <Button 
                    variant="primary" 
                    onClick={handleSaveEdit} 
                    className="edit-button save"
                  >
                    {t('warehouseConversation.save')}
                  </Button>
                  <Button 
                    variant="secondary" 
                    onClick={handleCancelEdit} 
                    className="edit-button cancel"
                  >
                    {t('warehouseConversation.cancel')}
                  </Button>
                </div>
              </>
            ) : (
              <div className="params-content">
                {/* Common warehouse parameters */}
                {displayParams.warehouse_reference && (
                  <div className="param-item">
                    <span className="param-label">{t('warehouseConversation.warehouseReference')}:</span>
                    <span className="param-value">{displayParams.warehouse_reference}</span>
                  </div>
                )}
                {displayParams.tracking_number && (
                  <div className="param-item">
                    <span className="param-label">{t('warehouseConversation.trackingNumber')}:</span>
                    <span className="param-value">{displayParams.tracking_number}</span>
                  </div>
                )}
                {displayParams.issue_type && (
                  <div className="param-item">
                    <span className="param-label">{t('warehouseConversation.issueType')}:</span>
                    <span className="param-value">{displayParams.issue_type}</span>
                  </div>
                )}
                {displayParams.priority && (
                  <div className="param-item">
                    <span className="param-label">{t('warehouseConversation.priority')}:</span>
                    <span className="param-value">{displayParams.priority}</span>
                  </div>
                )}
                {displayParams.description && (
                  <div className="param-item">
                    <span className="param-label">{t('warehouseConversation.description')}:</span>
                    <span className="param-value">{displayParams.description}</span>
                  </div>
                )}
                {displayParams.requested_action && (
                  <div className="param-item">
                    <span className="param-label">{t('warehouseConversation.requestedAction')}:</span>
                    <span className="param-value">{displayParams.requested_action}</span>
                  </div>
                )}
                {displayParams.warehouse_location && (
                  <div className="param-item">
                    <span className="param-label">{t('warehouseConversation.warehouseLocation')}:</span>
                    <span className="param-value">{displayParams.warehouse_location}</span>
                  </div>
                )}
                {conversation.status !== 'completed' && (
                  <Button 
                    variant="tertiary" 
                    onClick={handleEditClick} 
                    className="edit-button"
                  >
                    <FaEdit /> {t('warehouseConversation.edit')}
                  </Button>
                )}
              </div>
            )}
          </div>
        )}

        <div className="warehouse-conversation-messages">
          {conversation.messages.map((message, index) => (
            <div
              key={index}
              className={`warehouse-conversation-message warehouse-conversation-message--${message.direction}`}
            >
              <div className="warehouse-conversation-message__sender">
                <div className="sender-info">
                  {message.direction === 'received' ? (
                    <>
                      <FaWarehouse className="sender-icon" />
                      {t('warehouseConversation.warehouse')}
                    </>
                  ) : (
                    <>
                      <FaUser className="sender-icon" />
                      {t('warehouseConversation.us')}
                    </>
                  )}
                </div>
              </div>
              <div className="warehouse-conversation-message__content">
                {isEditing && index === 0 ? (
                  <textarea
                    value={editedMessage}
                    onChange={(e) => setEditedMessage(e.target.value)}
                    className="message-edit-textarea"
                    rows={4}
                  />
                ) : (
                  <div className="warehouse-response">
                    {message.direction === 'received' ? (
                      <div className="warehouse-response__details">
                        <div className="warehouse-response__details-label">
                          {t('warehouseConversation.details')}:
                        </div>
                        <div className="warehouse-response__details-content">
                          {parseWarehouseResponse(message.content).details}
                        </div>
                      </div>
                    ) : (
                      <div className="warehouse-response__details-content">
                        {message.content}
                      </div>
                    )}
                  </div>
                )}
              </div>
              <div className="warehouse-conversation-message__meta">
                <span className="warehouse-conversation-message__timestamp">
                  {formatTimestamp(message.timestamp)}
                </span>
                {message.status && (
                  <span className="warehouse-conversation-message__status">
                    {t(`warehouseConversation.status.${message.status}`)}
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>

        {conversation.status && (
          <div className="warehouse-conversation-status">
            <span className={`status-badge status-${conversation.status}`}>
              {t(`warehouseConversation.conversationStatus.${conversation.status}`, conversation.status)}
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

export default WarehouseConversation;